import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMode } from "@/context/ModeContext";
import { filtersToParams } from "@/lib/search";
import { readSavedSearches, restoreSavedSearch, savedSearchSummary } from "@/lib/savedSearches";
import type { SavedSearch } from "@/lib/savedSearches";

interface SavedSearchesMenuProps {
  onClose: () => void;
  onBack: () => void;
}

/**
 * ヘッダーメニューの「保存した検索条件」。
 * メニューを開いたときだけマウントし、その時点で localStorage から読み出す。
 */
export default function SavedSearchesMenu({ onClose, onBack }: SavedSearchesMenuProps) {
  const navigate = useNavigate();
  const { mode, setMode } = useMode();
  const [searches] = useState<SavedSearch[]>(() => readSavedSearches());

  const apply = (saved: SavedSearch) => {
    const restored = restoreSavedSearch(saved);
    if (saved.mode !== mode) setMode(saved.mode);
    onClose();
    navigate(`/search?${filtersToParams(restored.filters, restored.sort, 1).toString()}`);
  };

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-background-200 px-1 pb-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="メニューに戻る"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950"
        >
          <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
        </button>
        <span className="text-[13px] font-semibold text-foreground-950">保存した検索条件</span>
        <span className="ml-auto rounded-full bg-background-100 px-1.5 text-[10px] font-medium text-foreground-600">
          {searches.length}
        </span>
      </div>

      {searches.length === 0 ? (
        <div className="px-2 py-6 text-center">
          <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-lg text-foreground-500">
            <i className="ri-bookmark-3-line" aria-hidden="true" />
          </span>
          <p className="mt-2 text-[12px] font-medium text-foreground-900">保存した検索条件はまだありません</p>
          <p className="mt-1 text-[11px] leading-5 text-foreground-600">
            検索結果ページで条件を決めてから「検索条件を保存」を押すと、ここからワンクリックで呼び出せます。
          </p>
          <Link
            to="/search"
            onClick={onClose}
            className="mt-3 inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-search-line" aria-hidden="true" />
            作品を検索する
          </Link>
        </div>
      ) : (
        <ul className="mt-2 max-h-[60vh] space-y-1 overflow-y-auto pr-0.5">
          {searches.map((saved) => (
            <li key={saved.id}>
              <button
                type="button"
                onClick={() => apply(saved)}
                title="この条件で検索する"
                className="group w-full cursor-pointer rounded-md px-2.5 py-2 text-left transition-colors hover:bg-background-100"
              >
                <span className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-foreground-950 group-hover:text-primary-700">
                    {saved.name}
                  </span>
                  {saved.mode !== mode ? (
                    <span className="shrink-0 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                      {saved.mode === "female" ? "女性向け" : "男性向け"}
                    </span>
                  ) : null}
                  <i className="ri-arrow-right-s-line shrink-0 text-foreground-400" aria-hidden="true" />
                </span>
                <span className="mt-0.5 line-clamp-1 block text-[11px] text-foreground-500">
                  {savedSearchSummary(saved)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {searches.length > 0 ? (
        <div className="mt-2 border-t border-background-200 px-1 pt-2">
          <Link
            to="/search"
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-search-line" aria-hidden="true" />
            条件を変えて検索する
          </Link>
          <Link
            to="/settings"
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-settings-3-line" aria-hidden="true" />
            名前の変更・削除は設定画面から
          </Link>
        </div>
      ) : null}
    </div>
  );
}